import Logger from '../utils/logger';
import Directive from './directive';
import Event from '../utils/event';
import {SYSTEM_EVENTS} from '../events/events';
import _ from '../utils/underscore';
import Device from '../utils/device';
import DOM from '../utils/dom';
import ConfigService from '../services/config/config_service';
import Loader from '../utils/module_loader';
import ServicesRegistry from '../services/services_registry';

const CGP_START_SELECTOR = 'cgp-',
    CLASS_NAME = 'INITIALIZE',
    REPLACE_SERVICE = 'Service',
    EMPTY = '';


/**
 * Initialize class handle of creation core components in the application.
 * There are 3 types of supported components in the system:
 *  1. Directive
 *  2. Service
 *  3. Component
 *
 * All of the above initialized here with the properties model along. All of these are singletons.
 * Creation is done once.
 *
 * Usage:
 * When we create at the start of CGP initialization we create this class. It runs once and dies.
 *
 *
 */
class Initialize {
    /**
     * @type {Logger}
     */
    logger;

    /**
     * Init process, calls bootstrap to initiate the process.
     *
     * @constructor
     */
    constructor(onInitCompleteCallback) {
        let configs = ConfigService.create().getConfig();
        let loader = new Loader();
        this.logger = new Logger(CLASS_NAME, window.LOG_LEVEL);
        try {
            this.properties = configs;
            this.services = loader.services();
            this._initializeServices();
            this.loadDeviceSetting();
            this.subscribe();
            this._registerAllServices();
            this._doServicesAsyncJobs().then(() => {
                this._executeServicesOnInitializationCompleted().then(() => {
                    Event.fire(SYSTEM_EVENTS.ON_INIT_COMPLETE);
                    if (_.isFunction(onInitCompleteCallback)) {
                        onInitCompleteCallback();
                    }
                });
            });
        } catch (e) {
            this.logger.error('Error in SDK Initialize, error message:', e);
        }
    }


    /**
     * Subscribe initialize application startup events.
     */
    subscribe() {
        Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, this.loadDirective.bind(this));
    }

    /**
     * Loads all the services
     *
     * @public
     */
    _initializeServices() {
        try {

            let initializeService = fn => {
                try {
                    if (_.isFunction(fn.create)) {
                        fn.create(this._getClassProperties(fn));
                    } else {
                        this.logger.error('loadServices - ' + fn.CLASS + ' not created');
                    }
                } catch (ex) {
                    this.logger.error('Failed to load function service ', fn.CLASS, ex);
                }
            };

            this.services.forEach(service => {
                if (_.isArray(service)) {
                    service.forEach(fn => {
                        initializeService(fn);
                    });
                } else {
                    initializeService(service);
                }

            });
        } catch (ex) {
            this.logger.error('Failed to load services => ', ex);
        }
    }


    /**
     * Load directive class to maintain cgp dom components.
     *
     * @public
     */
    loadDirective() {
        try {
            Directive.create(Object.assign({}, this.properties.defaults));
        } catch (ex) {
            this.logger.error('Failed to load directives: ', ex);
        }
    }


    /**
     *  Load device settings selectors helper.
     */
    loadDeviceSetting() {
        let deviceSettings = Device.getDeviceSystemData();
        Object.keys(deviceSettings)
            .forEach((deviceSetting) => {
                if (_.isUndefined(deviceSettings[deviceSetting]) || _.isBoolean(deviceSettings[deviceSetting])) {
                    return;
                }

                DOM.addClass(DOM.getHTML(), CGP_START_SELECTOR + deviceSettings[deviceSetting]);
            });
    }


    /**
     * Getter for the class properties, passes externally during initialization.
     *
     * @param {Object} classObject - class reference of an un-instanced class.
     * @return {Object} of properties that reference only this class.
     * @public
     */
    _getClassProperties(classObject) {
        let objectName = classObject.CLASS
            .replace(REPLACE_SERVICE, EMPTY);

        return Object.assign({},
            this.properties.defaults,
            this.properties[objectName]);
    }

    _registerAllServices() {
        let servicesRegistry = ServicesRegistry.create();
        this.services.forEach(srv => {
            servicesRegistry.registerService(srv);
        });
    }

    /**
     * start services async jobs ( every service will implement his own async jobs ).
     *
     * @private
     */
    _doServicesAsyncJobs() {
        let asyncJobsPromises = [];

        let servicesRegistry = ServicesRegistry.create();
        this.services.forEach(service => {
            try {
                // push promise result from each service
                let serviceInstance = servicesRegistry.getService(service.CLASS);
                asyncJobsPromises.push(serviceInstance.doAsyncJobs());
            } catch (ex) {
                this.logger.error('Failed to get new promise from service: ' + service.CLASS, ex);
            }
        });

        return Promise.all(asyncJobsPromises)
            .catch(err => {
                this.logger.error('Failed to finish all services async jobs: ', err);
            });

    }

    _executeServicesOnInitializationCompleted() {
        let promises = [];
        let servicesRegistry = ServicesRegistry.create();
        this.services.forEach(service => {
            try {
                let serviceInstance = servicesRegistry.getService(service.CLASS);
                promises.push(serviceInstance.onInitializationCompleted());
            } catch (ex) {
                this.logger.error('_executeServicesOnInitializationCompleted failed for service: ' + service.CLASS, ex);
            }
        });

        return Promise.all(promises)
            .catch(err => {
                this.logger.error('_executeServicesOnInitializationCompleted failed:', err);
            });
    }
}

export default Initialize;
export {Loader};